import {
	Component,
	computed,
	input,
	linkedSignal,
	output,
	signal,
	viewChild,
	ViewEncapsulation,
} from '@angular/core';

import { SliderNavButtonComponent } from '@valk-nx/components/ui-slider/src/lib/nav-button/nav-button';
import { SliderComponent } from '@valk-nx/components/ui-slider/src/lib/slider';
import type { SliderConfig } from '@valk-nx/components/ui-slider/src/lib/slider.interface';
import { cn } from '@valk-nx/core/lib/core';
import { LazyImageLoadingDirective } from '@valk-nx/core/lib/directives/lazy-image/lazy-image.directive';
import type { PhotoInterface } from '@valk-nx/core/lib/interfaces/image.interface';
import { ImageQueryPipe } from '@valk-nx/core/lib/pipes/image-query/image-query.pipe';
import { injectBodyLock } from '@valk-nx/helpers/lib/body-lock/body-lock.service';
import { SVG_ICON_IMPORTS } from '@valk-nx/helpers/lib/svg-unique-ids/svg-unique-ids.directive';

const FALLBACK_IMAGE_SRC =
	'/assets/images/application-icons/icon-transparent-96x96.png';

/** One image per view with a gap wide enough to keep neighbours out of frame. */
const MODAL_SLIDER_CONFIG: SliderConfig = {
	slidesPerView: 1,
	gap: 50,
	edgeOffset: 0,
	loop: true,
	showNavigation: false,
	showPagination: false,
	breakpoints: {},
};

export interface CarouselModalEvent {
	index: number | undefined;
	isOpen: boolean;
	function: string;
}

@Component({
	encapsulation: ViewEncapsulation.None,
	selector: 'vp-carousel-modal',
	templateUrl: './carousel-modal.html',
	host: { '(window:keydown.escape)': 'toggleCarousel($event)' },
	imports: [
		SVG_ICON_IMPORTS,
		ImageQueryPipe,
		LazyImageLoadingDirective,
		SliderComponent,
		SliderNavButtonComponent,
	],
})
export class CarouselModalComponent {
	readonly config = input<SliderConfig>({});
	readonly images = input<PhotoInterface[]>([]);
	readonly showCarousel = input(false);
	readonly className = input('');

	readonly carouselEmitter = output<CarouselModalEvent>();

	readonly slider = viewChild(SliderComponent);

	readonly isOpen = linkedSignal(() => this.showCarousel());
	readonly loadedIndexes = signal(new Set<number>());
	readonly failedIndexes = signal(new Set<number>());

	readonly sliderConfig = computed<SliderConfig>(() => ({
		...MODAL_SLIDER_CONFIG,
		...this.config(),
	}));

	readonly slides = computed<PhotoInterface[]>(() =>
		this.images().map((image, index) =>
			this.failedIndexes().has(index)
				? { ...image, src: FALLBACK_IMAGE_SRC }
				: image,
		),
	);

	protected readonly cn = cn;

	private readonly bodyLock = injectBodyLock();

	onImageLoad(index: number) {
		this.loadedIndexes.update((loaded) => new Set(loaded).add(index));
	}

	setImageFallback(index: number) {
		this.failedIndexes.update((failed) => new Set(failed).add(index));
	}

	slideNext() {
		this.slider()?.scrollNext();
		this.emitState('slideNext');
	}

	slidePrev() {
		this.slider()?.scrollPrev();
		this.emitState('slidePrev');
	}

	toggleCarousel(event?: MouseEvent | Event): void {
		const target = event?.target as unknown as {
			getAttribute: (attr: string) => boolean;
		};
		const keyBoardEvent = event as KeyboardEvent;

		if (
			!event ||
			target.getAttribute('data-close-carousel') ||
			(keyBoardEvent?.key === 'Escape' && this.isOpen())
		) {
			this.isOpen.update((open) => !open);
			this.bodyLock.toggle(this.isOpen());

			this.emitState('toggleCarousel');
		}
	}

	private emitState(fn: string) {
		this.carouselEmitter.emit({
			index: this.slider()?.selectedSnap() ?? 0,
			isOpen: this.isOpen(),
			function: fn,
		});
	}
}
